/**
 * Operator role context.
 *
 * Populated by <RequireRole/> after GET /v1/admin/me resolves the caller's
 * role. Descendant components (top bar role badge, write-action guards) read
 * it via useAdminRole(). The role displayed here is presentation only — every
 * admin route enforces the role SERVER-SIDE (admin = read/write,
 * support = read-only).
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { AdminRole } from '@/api/admin-types';

export interface AdminRoleValue {
  role: AdminRole;
  clerkUserID: string;
}

const AdminRoleContext = createContext<AdminRoleValue | null>(null);

export function AdminRoleProvider({
  value,
  children,
}: {
  value: AdminRoleValue;
  children: ReactNode;
}) {
  return <AdminRoleContext.Provider value={value}>{children}</AdminRoleContext.Provider>;
}

export function useAdminRole(): AdminRoleValue {
  const value = useContext(AdminRoleContext);
  if (!value) {
    throw new Error('useAdminRole must be used within <AdminRoleProvider>.');
  }
  return value;
}
